import type React from 'react'
import { useState, useEffect } from 'react'

type PdfMapType = {
  maps: { name: string; map: string }[]
  mapCaption: string
}

/**
 * Safariだけ挙動が違うので、新しいタブで出したいだけなのに、
 * とても大袈裟なコードを書いていることに注意。
 * */
export const PdfSelect: React.FC<PdfMapType> = ({ maps, mapCaption }) => {
  const [selectedValue, setSelectedValue] = useState<string>('default')
  const [selectedUrl, setSelectedUrl] = useState<string>('default')
  const [time, setTime] = useState(new Date().getTime())

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    // onChangeだとiPhoneでポップアップが表示されないため、同じ画面で開くようにしている。
    // onClickだと、window.openが動くが、selectだと動かないので致し方なし。
    setSelectedValue(() => 'default')
    setSelectedUrl(() => e.target.value)
    setTime(new Date().getTime())
  }

  const handleClick = () => {
    // iPhoneで同じ値を選択した時にonChangeが動かない問題を解決
    // 強制的に再レンダリングして状態をリセット
    setSelectedValue('default')
  }

  useEffect(() => {
    // 上記のonChangeを使っていると、SelectBoxの選択肢が変わらない場合、リンクが開かない。
    // そのため、本当にしたくないのだけれど、useEffectを使って無理くり新しいページ開くようにしている。
    if (selectedUrl === 'default') return
    // iPhoneでもポップアップを表示するため、ユーザーインタラクション内でwindow.openを実行
    setSelectedValue(() => 'default')
    try {
      // 直接URLを指定してwindow.openを実行
      const windowReference = window.open(selectedUrl, '_blank')

      if (windowReference) {
        // console.log('ポップアップが開かれました')
        // ポップアップが開かれた場合、フォーカスを設定
        windowReference.focus()
      } else {
        // ポップアップがブロックされた場合、直接リンクを使用
        // console.log('ポップアップがブロックされました。直接リンクを使用します')
        window.location.href = selectedUrl
      }
    } catch (error) {
      // エラーが発生した場合も直接リンクを使用
      // console.log('エラーが発生しました。直接リンクを使用します:', error)
      window.location.href = selectedUrl
    }
    setSelectedUrl('default')
  }, [time])

  if (!maps || maps.length === 0) {
    return null
  }

  return (
    <>
      <div className="bg-white rounded">
        <div className="flex items-center gap-0.5 pl-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="icon icon-tabler icons-tabler-outline icon-tabler-file-type-pdf"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
            <path d="M5 12v-7a2 2 0 0 1 2 -2h7l5 5v4" />
            <path d="M5 18h1.5a1.5 1.5 0 0 0 0 -3h-1.5v6" />
            <path d="M17 18h2" />
            <path d="M20 15h-3v6" />
            <path d="M11 15v6h1a2 2 0 0 0 2 -2v-2a2 2 0 0 0 -2 -2h-1z" />
          </svg>
          <select
            key={`select-${time}`}
            className="border-none text-xs font-bold w-full py-0.5 overflow-hidden text-ellipsis whitespace-nowrap"
            value={selectedValue}
            onClick={handleClick}
            onChange={handleChange}
          >
            <option value={'default'} disabled>
              {mapCaption}
            </option>

            {maps.map((val: { name: string; map: string }, i: number) => {
              return (
                <option key={`${i}-${time}`} value={val.map}>
                  {val.name}
                </option>
              )
            })}
          </select>
        </div>
      </div>
    </>
  )
}
